import {
  ANALYTICS_EVENT,
  ANALYTICS_PROPERTY,
  GetCustomQuestionsPublicEndpoint,
  WaitlistMode,
  ResourceGroupV3,
} from '@kouto/types';
import { validateAccessCode } from 'actions/access-code';
import { filterEmpty } from 'actions/helpers';
import {
  integrateSessionParticipants,
  setTierParticipants,
} from 'actions/participants';
import { addItemToWaitlist } from 'actions/waitlist';
import { useAppState, useDispatch } from 'AppProvider';
import {
  BottomDrawer,
  BottomDrawerProps,
} from 'components/molecules/BottomDrawer';
import useBrandToggleFeature from 'components/BrandToggleFeature/use-brand-toggle-feature';
import ResourceAccessCode, {
  AccessCodeHeading,
} from 'components/Resource/ResourceAccessCode';
import { PrimaryButton } from 'components/theme/Button/Button';
import {
  analyticsManager,
  getAnalyticsDataFromCartItem,
  getAnalyticsItemsFromProductsListing,
  getAnalyticsListNameFromProductLine,
} from 'features/analytics';
import { usePageViewEvent } from 'hooks/usePageViewEvent';
import { PageTitle } from 'components/atoms/PageTitle';
import useEventBookingSession from 'features/Events/hooks/useEventBookingSession';
import useSearchQueryParams from 'hooks/use-search-params';
import { useTierParticipants } from 'hooks/use-selected-participants';
import { useCart } from 'hooks/useCart';
import moment from 'moment';
import React, {
  PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import useFetchWaitlist from 'selectors/waitlist';
import styled from 'styled-components';
import { VALIDATE_ACCESS_CODE_FAILURE } from 'types/access-code';
import { CartItemState } from 'types/cart';
import { Session } from 'types/listings';
import { BookingMode } from 'types/payment';
import { IWaitlistState } from 'types/waitlist';
import { DATE_FORMAT, serializeParams, TIME_FORMAT, uuidv4 } from 'utils';
import { useIsMobile } from 'WindowDimensionProvider';
import useWaitList from 'api/waitlist/useWaitList';
import { useGlobalDateLocale } from 'context/DateLocaleContext';
import { formatTimeShort } from 'utils/dateTime';
import getUpdatedSelectedDate from 'utils/data/getUpdatedSelectedData';
import getCustomQuestions from 'api/experience/useCustomQuestions/getCustomQuestions';
import hasParticipantOrCustomQuestion from 'utils/customQuestions/hasParticipantOrCustomQuestion';
import { ResourceGroupExperienceV3 } from 'types/listingV3';
import getFirstAvailableDateFromSessions from 'utils/data/firstAvailableDate/getFirstAvailableDateFromSessions';
import bookingAvailabilityModeToBookingMode from 'utils/data/booking/bookingAvailabilityModeToBookingMode';
import useBookableAddOns from 'api/listing/useBookableAddOns';
import {
  TicketHeader,
  TicketPricesSelector,
  TicketSummary,
  TicketTimesSelector,
} from './components';
import {
  getMaxQuantitiesPerTiersLists,
  getNumberOfParticipantsFromPriceTier,
} from './utils/tickets-utils';

interface PriceTiersWithParticipantsProps {
  selectedNumber: number;
  participants: number;
  id: string;
  name: string;
  price: number;
  maxQuantity?: number | undefined;
}

interface EventTicketsPageProps extends BottomDrawerProps {}

const EventTicketsPage = (props: EventTicketsPageProps) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { t } = useTranslation();
  const { locale } = useGlobalDateLocale();
  const { listing, daySessions, latest, date } = useEventBookingSession();
  const { data: waitlist } = useWaitList(listing?.waitlistId, listing?.brandId);
  const waitlistEnabled = !!waitlist && waitlist?.mode !== WaitlistMode.NONE;
  const firstAvailableScheduledDate =
    getFirstAvailableDateFromSessions(daySessions)?.startDateTime?.split(
      'T',
    )[0];
  const selectedDate = getUpdatedSelectedDate(
    date,
    firstAvailableScheduledDate,
    waitlist,
  );

  const [priceTierSelection, setPriceTierSelection] = useState<
    { id: string; numberOfParticipants: number }[]
  >([]);
  const [selectedTime, setSelectedTime] = useState<string | null>(null);
  const selectedAddOnSession = daySessions.find(
    (session) => session.startDateTime.split('T')[1] === selectedTime,
  );
  const hasBookableAddOns = useBookableAddOns({
    listingId: listing?.id,
    experienceId: selectedAddOnSession?.experienceId,
    startDateTime: selectedAddOnSession?.startDateTime,
    sessionDuration: selectedAddOnSession?.duration,
    hasAddOns: !!listing?.addOns?.length,
  });
  const isCartFeatureEnabled = useBrandToggleFeature('shoppingCart');
  const { searchParams } = useSearchQueryParams();
  const [, setSelectedTierCustomers] = useTierParticipants();
  const { onAddToCart } = useCart();
  useFetchWaitlist();

  const isMobileView = useIsMobile();

  // Ticket selection had no page-view tracking, so Adobe never received `property_page` with
  // pageDetail1 `tickets_selection` (BRD 2.16). VIEW_CALENDAR is the event mapped to that bucket
  // in AdobeLaunchClient's PAGE_LOAD_MAPPING. Keyed on the selected date so changing the date
  // re-fires the view; the Adobe client's own digest still drops identical consecutive sends.
  usePageViewEvent({
    eventName: ANALYTICS_EVENT.VIEW_CALENDAR,
    isNotReady: !listing,
    key: `${listing?.id}-${selectedDate}`,
    payload: {
      [ANALYTICS_PROPERTY.ExperienceId]: listing?.id,
    },
    // Everything the page view can do without lives here: `payload` keys gate the send, so a
    // listing with no category or title would otherwise suppress the beacon entirely.
    optionalPayload: {
      [ANALYTICS_PROPERTY.ExperienceCategory]:
        listing?.category?.name ?? undefined,
      [ANALYTICS_PROPERTY.ExperienceTitle]: listing?.title ?? undefined,
      [ANALYTICS_PROPERTY.ExperienceProduct]: listing?.productLine,
      [ANALYTICS_PROPERTY.ListName]: getAnalyticsListNameFromProductLine(
        listing?.productLine,
      ),
      [ANALYTICS_PROPERTY.PageName]: `${t('tickets')} | ${
        listing?.title ?? ''
      }`,
      [ANALYTICS_PROPERTY.PageType]: 'Ticket Selection Page',
      [ANALYTICS_PROPERTY.Products]: JSON.stringify(
        getAnalyticsItemsFromProductsListing({
          listings: listing ? [listing] : [],
          product: listing?.productLine,
        }),
      ),
    },
  });

  const [accessCodeError, setAccessCodeError] = useState('');
  const [isCodeValidated, setIsAccessCodeValidated] = useState(false);
  // Ref, not state: the validated code is read later in the same click handler
  // that triggered validation, before the next render.
  const validatedAccessCode = useRef<{
    code: string;
    cartItemMetadata?: { experienceIds?: string[] };
  } | null>(null);

  const isEventExclusive = listing?.isExclusive;
  const isAccessCodeVerified = isEventExclusive ? isCodeValidated : true;

  const allExperiences = useMemo(() => {
    return (
      listing?.resourceGroups?.flatMap(
        (resourceGroup) => resourceGroup.experiences,
      ) || []
    );
  }, [listing?.resourceGroups]);

  const { availableStartTimes, waitlistTimes, priceTiers } = useMemo(() => {
    return getMaxQuantitiesPerTiersLists({
      experiences: allExperiences.map((exp) => ({
        ...exp,
        id: exp.id,
        maximumAllowedToBook: exp.maximumAllowedToBook ?? null,
        minimumRequiredToBook: exp.minimumRequiredToBook ?? null,
      })),
      sessions: daySessions,
      priceTiersSelection: priceTierSelection,
      waitlistEnabled,
    });
  }, [allExperiences, daySessions, priceTierSelection, waitlistEnabled]);

  const waitlistTimesList = useMemo(() => {
    return waitlistTimes.map((time) => ({
      value: time,
      label: formatTimeShort(time, locale),
    }));
  }, [waitlistTimes, locale]);

  const priceTiersWithParticipants = useMemo(() => {
    return priceTierSelection.flatMap((selectedPriceTier) => {
      const priceTier = priceTiers.find(
        ({ id }) => id === selectedPriceTier.id,
      );

      return !priceTier
        ? []
        : [
            {
              ...priceTier,
              participants: selectedPriceTier.numberOfParticipants,
              maxQuantity: priceTier.maxQuantity ?? undefined,
            },
          ];
    });
  }, [priceTierSelection, priceTiers]);

  const isWaitlistSelected = useMemo(
    () => selectedTime && waitlistTimes.includes(selectedTime),
    [waitlistTimes, selectedTime],
  );

  const directCheckoutFlow = useCallback(
    async ({
      listingId,
      session,
      experience,
      resourceGroup,
      priceTiersWithParticipants,
    }: {
      listingId?: string;
      session: Session;
      experience: ResourceGroupExperienceV3;
      resourceGroup: ResourceGroupV3;
      priceTiersWithParticipants: PriceTiersWithParticipantsProps[];
    }) => {
      const participants = priceTiersWithParticipants.flatMap((p) =>
        new Array(p.participants).fill(p).map(() => ({
          fullName: 'Resource Purchaser',
          firstName: 'Resource',
          lastName: 'Purchaser',
          terms: false,
          price: p.price,
          priceTierName: p.name || '',
        })),
      );

      if (waitlistEnabled && isWaitlistSelected) {
        dispatch(
          addItemToWaitlist({
            item: {
              cartItemId: uuidv4(),
              listingId,
              state: CartItemState.READY,
              sessionDateTime: session.startDateTime,
              sessionDuration: moment
                .duration(session.duration, 'minutes')
                .toISOString(),
              experienceId: experience.id,
              bookingMode: searchParams.mode || BookingMode.SHARED,
              notes: '',
              participants,
              additionalCustomQuestionResponses: {},
            },
          }),
        );
      } else {
        setSelectedTierCustomers({
          selectedParticipants: priceTiersWithParticipants,
          currentExperience: experience.id,
        });

        const cartData = {
          state: CartItemState.READY,
          participants,
          notes: '',
          maxParticipantReached: '',
          bookingMode: bookingAvailabilityModeToBookingMode(
            experience.bookingAvailabilityMode,
          ),
          listingId: listing?.id ?? undefined,
          resourceGroupCollectionId:
            listing?.resourceGroupCollectionId ?? undefined,
          experienceId: experience?.id,
          groupId: resourceGroup.id,
          additionalCustomQuestionResponses: {},
          sessionDateTime: `${selectedDate}T${
            session?.startDateTime
              ? moment(session.startDateTime).format(TIME_FORMAT)
              : undefined
          }`,
          sessionDuration: session?.duration
            ? moment.duration(session?.duration, 'minutes').toISOString()
            : '',
          timezone: listing?.location?.timezone,
        };

        if (listing) {
          analyticsManager.sendEvent(
            ANALYTICS_EVENT.CLICK_ADD_TO_CART_BUTTON,
            getAnalyticsDataFromCartItem({
              cartData,
              locationName: listing.location?.name,
              collection: {
                id: listing.id,
                category: {
                  name: listing.category?.name ?? 'Category not found',
                  id: listing.category?.id ?? 'Category ID not found',
                },
                title: listing.title ?? 'Title not found',
              },
              resourceGroupTitle: resourceGroup.title ?? undefined,
              experienceId: experience.id,
            }),
          );
          analyticsManager.sendEvent(
            ANALYTICS_EVENT.CLICK_PROCEED_TO_CHECKOUT_BUTTON,
            getAnalyticsDataFromCartItem({
              cartData,
              locationName: listing.location?.name,
              collection: {
                id: listing.id,
                category: {
                  name: listing.category?.name ?? 'Category not found',
                  id: listing.category?.id ?? 'Category ID not found',
                },
                title: listing.title ?? 'Title not found',
              },
              resourceGroupTitle: resourceGroup.title ?? undefined,
              experienceId: experience.id,
            }),
          );
        }
        const addedEvent = {
          experienceIds: new Set(
            resourceGroup.experiences.map((e) => e.id),
          ) as Set<string>,
          hasResourceGroups: !!resourceGroup,
          experienceIdToMaxParticipants: resourceGroup.experiences.reduce(
            (acc, e) => {
              acc[e.id] = e.maxParticipantCount ?? 0;
              return acc;
            },
            {} as Record<string, number>,
          ),
          selectedTime,
          numberOfParticipants:
            getNumberOfParticipantsFromPriceTier(priceTierSelection),
        };
        await onAddToCart(cartData, '/checkout', undefined, addedEvent);
      }
    },
    [
      selectedDate,
      onAddToCart,
      setSelectedTierCustomers,
      listing,
      isWaitlistSelected,
      dispatch,
      searchParams,
      waitlistEnabled,
      selectedTime,
      priceTierSelection,
    ],
  );

  const addToCartAndRedirect = useCallback(
    ({
      experience,
      resourceGroup,
      session,
      route,
      state,
      skipParticipantsName = false,
      overrideSearch = '',
    }: {
      experience: ResourceGroupExperienceV3;
      resourceGroup: ResourceGroupV3;
      session: Session;
      route: string;
      state: CartItemState;
      skipParticipantsName?: boolean;
      overrideSearch?: string;
    }) => {
      const participants = priceTiersWithParticipants.flatMap((p) =>
        new Array(p.participants).fill(p).map(() => ({
          fullName: skipParticipantsName ? 'Resource Purchaser' : '',
          firstName: skipParticipantsName ? 'Resource' : '',
          lastName: skipParticipantsName ? 'Purchaser' : '',
          terms: false,
          price: p.price,
          priceTierName: p.name || '',
        })),
      );

      const cartData = {
        state,
        participants,
        notes: '',
        maxParticipantReached: '',
        bookingMode: bookingAvailabilityModeToBookingMode(
          experience.bookingAvailabilityMode,
        ),
        listingId: listing?.id,
        experienceId: experience?.id,
        resourceGroupCollectionId:
          listing?.resourceGroupCollectionId ?? undefined,
        groupId: resourceGroup.id,
        additionalCustomQuestionResponses: {},
        sessionDateTime: `${selectedDate}T${
          session?.startDateTime
            ? moment(session.startDateTime).format(TIME_FORMAT)
            : undefined
        }`,
        sessionDuration: session?.duration
          ? moment.duration(session?.duration, 'minutes').toISOString()
          : '',
        timezone: listing?.location?.timezone,
        ...(validatedAccessCode.current?.cartItemMetadata?.experienceIds?.includes(
          experience.id,
        ) && { accessCode: validatedAccessCode.current.code }),
      };

      const addedEvent = {
        experienceIds: new Set(
          resourceGroup.experiences.map((e) => e.id),
        ) as Set<string>,
        hasResourceGroups: !!resourceGroup,
        experienceIdToMaxParticipants: resourceGroup.experiences.reduce(
          (acc, e) => {
            acc[e.id] = e.maxParticipantCount ?? 0;
            return acc;
          },
          {} as Record<string, number>,
        ),
        selectedTime,
        numberOfParticipants:
          getNumberOfParticipantsFromPriceTier(priceTierSelection),
      };
      onAddToCart(cartData, route, overrideSearch, addedEvent);
    },
    [
      listing,
      onAddToCart,
      priceTiersWithParticipants,
      selectedDate,
      selectedTime,
      priceTierSelection,
    ],
  );

  const defaultCheckoutFlow = useCallback(
    ({
      noOfGuests,
      session,
      resourceGroup,
      experience,
      priceTiersWithParticipants,
      skipParticipantsName,
    }: {
      noOfGuests: number;
      session: Session;
      resourceGroup: ResourceGroupV3;
      experience: ResourceGroupExperienceV3;
      priceTiersWithParticipants: PriceTiersWithParticipantsProps[];
      skipParticipantsName: boolean;
    }) => {
      const bookingData = {
        mode: bookingAvailabilityModeToBookingMode(
          experience.bookingAvailabilityMode,
        ),
        noOfGuests,
        sessionDate: moment(session.startDateTime).format(DATE_FORMAT),
        sessionTime: moment(session.startDateTime).format(TIME_FORMAT),
        duration: moment.duration(session.duration, 'minutes').toISOString(),
        supportedParticipantsCount: resourceGroup.experiences.length,
        listingId: listing?.id,
        skipParticipantsName,
        waitlistId: listing?.waitlistId ?? undefined,
        groupId: resourceGroup.id,
        ...(isCartFeatureEnabled ? searchParams : {}),
      };

      setSelectedTierCustomers({
        selectedParticipants: priceTiersWithParticipants,
        currentExperience: experience.id,
        groupTitle: resourceGroup.title,
      });

      dispatch(
        integrateSessionParticipants({
          session,
        }),
      );

      dispatch(
        setTierParticipants({
          participants: priceTiersWithParticipants,
          currentExperience: experience.id,
          groupTitle: resourceGroup.title ?? undefined,
        }),
      );

      if (latest) {
        bookingData.latest = true;
      }
      const filteredBookingData = filterEmpty(bookingData);

      if (waitlistEnabled && isWaitlistSelected) {
        history.push({
          pathname: `/e/${experience.id}/waitlist`,
          search: serializeParams(filteredBookingData),
        });
      } else if (isCartFeatureEnabled) {
        const params = new URLSearchParams();
        params.set(
          'skipParticipantsName',
          skipParticipantsName ? 'true' : 'false',
        );
        if (selectedDate) {
          params.set('date', selectedDate);
        }
        if (latest) {
          params.set('latest', 'true');
        }

        addToCartAndRedirect({
          experience,
          resourceGroup,
          session,
          route: `/e/${experience.id}/participants/{cartItemId}`,
          state: CartItemState.PENDING,
          skipParticipantsName,
          overrideSearch: params.toString(),
        });
      } else {
        history.push({
          // TODO: check if this route is not used
          pathname: `/e/${experience.id}/booking`,
          search: serializeParams(filteredBookingData),
        });
      }
    },
    [
      dispatch,
      listing,
      latest,
      selectedDate,
      history,
      isCartFeatureEnabled,
      searchParams,
      setSelectedTierCustomers,
      addToCartAndRedirect,
      isWaitlistSelected,
      waitlistEnabled,
    ],
  );

  const { item: waitlistItem, experience: waitlistExperience }: IWaitlistState =
    useAppState((state: Record<string, unknown>) => state.waitlist);

  useEffect(() => {
    if (waitlistItem && waitlistExperience) {
      const params = { ...searchParams };
      if (listing?.waitlistId) {
        params.waitlistId = listing.waitlistId;
      }
      history.push({
        pathname: '/join-waitlist',
        search: `?${new URLSearchParams(params).toString()}`,
      });
    }
  }, [listing, waitlistItem, waitlistExperience, searchParams, history]);

  const selectedPriceTiersAmount = useMemo(
    () =>
      priceTierSelection.reduce((sum, tier) => {
        return sum + tier.numberOfParticipants;
      }, 0),
    [priceTierSelection],
  );

  const withAccessCodeValidation = async (
    code: string,
    onSuccess: CallableFunction,
  ) => {
    if (!isEventExclusive || isAccessCodeVerified) {
      onSuccess();
      return;
    }
    if (!code) {
      return;
    }

    const experiences = listing?.resourceGroups?.[0]?.experiences ?? [];

    const response = await dispatch(
      validateAccessCode({
        code,
        brandId: listing?.brandId,
        experienceId: experiences[0]?.id,
        experienceIds: experiences.map((e) => e.id),
        isCartFeatureEnabled,
      }),
    );
    if (response.type === VALIDATE_ACCESS_CODE_FAILURE) {
      setIsAccessCodeValidated(false);
      setAccessCodeError('Invalid code');
      return;
    }
    validatedAccessCode.current = response.payload?.accessCode ?? null;
    onSuccess();
  };

  const onContinueClick = useCallback(async () => {
    const session = daySessions.find(
      (s) => s.startDateTime.split('T')[1] === selectedTime,
    );

    const resourceGroup = listing?.resourceGroups?.[0];

    const experience = resourceGroup?.experiences.find(
      (e) => e.id === session?.experienceId,
    );

    const noOfGuests = priceTiersWithParticipants.reduce(
      (sum, data) => sum + data.participants,
      0,
    );

    if (
      !listing ||
      !session ||
      !experience ||
      !resourceGroup ||
      noOfGuests === 0
    ) {
      return;
    }
    const { addOns } = listing;
    const hasAddOns = addOns?.length > 0;

    let customQuestions;
    try {
      customQuestions = await getCustomQuestions(
        GetCustomQuestionsPublicEndpoint.url({
          brandId: listing?.brandId as string,
          experienceId: experience.id,
          query: {
            includeDefault: true, // Important for checkout flow.
          },
        }),
      );
    } catch (error) {
      console.error('Error fetching custom questions', error);
      customQuestions = undefined;
    }

    const { hasCustomQuestions, askParticipantsInfo } =
      hasParticipantOrCustomQuestion(customQuestions);

    const shouldOfferAddOns =
      hasAddOns && !(waitlistEnabled && isWaitlistSelected);
    if (shouldOfferAddOns && (await hasBookableAddOns())) {
      addToCartAndRedirect({
        experience,
        resourceGroup,
        session,
        route: '/addons/{cartItemId}',
        state: CartItemState.PENDING,
        skipParticipantsName: !askParticipantsInfo,
      });
    } else if (askParticipantsInfo || hasCustomQuestions) {
      defaultCheckoutFlow({
        noOfGuests,
        session,
        resourceGroup,
        experience,
        priceTiersWithParticipants: priceTiersWithParticipants.map((pt) => ({
          ...pt,
          selectedNumber: pt.participants,
        })),
        skipParticipantsName: !askParticipantsInfo,
      });
    } else {
      directCheckoutFlow({
        listingId: listing.id,
        session,
        experience,
        resourceGroup,
        priceTiersWithParticipants: priceTiersWithParticipants.map((pt) => ({
          ...pt,
          selectedNumber: pt.participants,
        })),
      });
    }
  }, [
    daySessions,
    listing,
    priceTiersWithParticipants,
    selectedTime,
    waitlistEnabled,
    isWaitlistSelected,
    defaultCheckoutFlow,
    directCheckoutFlow,
    addToCartAndRedirect,
    hasBookableAddOns,
  ]);

  const startTimesList = useMemo(() => {
    return availableStartTimes.map((time) => ({
      value: time,
      label: formatTimeShort(time, locale),
    }));
  }, [availableStartTimes, locale]);

  const allTimes = useMemo(
    () => [...startTimesList, ...waitlistTimesList],
    [startTimesList, waitlistTimesList],
  );

  const isOneTime = allTimes.length === 1;

  useEffect(() => {
    if (!isOneTime) return;
    setSelectedTime(allTimes[0].value);
  }, [allTimes, isOneTime]);
  if (isMobileView && !isAccessCodeVerified) {
    return (
      <BottomDrawer {...props} heading={<AccessCodeHeading />}>
        <ResourceAccessCode
          errorMessage={accessCodeError}
          withHeading={false}
          isExclusive={isEventExclusive}
          onContinue={(code) =>
            withAccessCodeValidation(code, () => {
              setIsAccessCodeValidated(true);
            })
          }
          customCallToAction={listing?.customCallToAction ?? null}
        />
      </BottomDrawer>
    );
  }
  return (
    <EventPageWrapper {...props}>
      {listing?.title && (
        <PageTitle translatedTitle={listing?.title} subTitle="tickets" />
      )}
      <EventTicketsWrapper>
        <TicketInfo>
          <TicketHeader selectedTime={selectedTime} />
          <TicketPricesSelector
            priceTiers={priceTiers}
            onChangePriceTiers={setPriceTierSelection}
            priceTierSelection={priceTierSelection}
          />
          <TicketTimesSelector
            selectedTime={selectedTime}
            selectedPriceTiersAmount={selectedPriceTiersAmount}
            availableTimes={startTimesList}
            waitlistTimes={waitlistTimesList}
            setSelectedTime={setSelectedTime}
          />
          <CheckoutButtonWrapper>
            <ResourceAccessCode
              errorMessage={accessCodeError}
              isExclusive={
                isMobileView ? !isAccessCodeVerified : isEventExclusive
              }
              renderActionButtons={(accessCode) => (
                <PrimaryButton
                  className="activate-checkout-button"
                  big
                  disabled={
                    !selectedTime ||
                    (startTimesList.length === 0 &&
                      waitlistTimesList.length === 0) ||
                    selectedPriceTiersAmount === 0
                  }
                  onClick={() =>
                    withAccessCodeValidation(accessCode, onContinueClick)
                  }
                >
                  {isWaitlistSelected ? t('joinWaitlist') : t('continue')}
                </PrimaryButton>
              )}
            />
          </CheckoutButtonWrapper>
        </TicketInfo>
        <TicketSummary priceTiers={priceTiersWithParticipants} />
      </EventTicketsWrapper>
    </EventPageWrapper>
  );
};

const EventPageWrapper = ({
  children,
  ...props
}: PropsWithChildren<BottomDrawerProps>) => {
  const isMobileView = useIsMobile();
  if (isMobileView) {
    return <BottomDrawer {...props}>{children}</BottomDrawer>;
  }
  return children;
};

const EventTicketsWrapper = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
  height: 100%;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

export const TicketInfo = styled.div`
  width: calc(100% - 350px);
  display: flex;
  flex-direction: column;
  padding-right: 48px;

  & > * {
    border-bottom: 0.5px solid var(--way-colors-borderColor);
    padding: 42px 0px;
  }
  & > *:first-child {
    padding: 0px 0px 42px;
  }

  @media (max-width: 768px) {
    width: 100%;
    padding-right: 0;

    & > * {
      border-bottom: 0.5px solid var(--way-colors-borderColor);
      padding: 24px 0px;
    }
    & > *:first-child {
      padding: 0px 0px 24px;
    }
  }
`;

const CheckoutButtonWrapper = styled.div`
  border-bottom: none;
  padding-bottom: 100px;

  @media (max-width: 768px) {
    padding-bottom: 24px;
  }
`;

export default EventTicketsPage;
